import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import DragHandle from 'react/elements/DragHandle';
import Modal from 'react/components/Modal';
import NamePicker from 'react/components/search/NamePicker';
import OptionsMenu from './OptionsMenu';
import { addMealIdeaToGroup, removeMealIdeaFromGroup, selectMealIdea } from 'redux/actions/mealIdeas';
import { selectMealIdeasIndex } from '../../../redux/food/selectors';
import useOffline, { onlineOnly } from '../../hooks/useOffline';

/**
 * One idea as it appears in one group. Clicking the name selects it, which opens it in the Meal Ideas
 * tab's popup, or calls `onOpen` with its id and the row element instead when that is given, the
 * row being where the popup grows from.
 * `data-id` and `data-name` are there for the drag-onto-calendar work, which reads them the way
 * `CalendarPage`'s ThirdPartyDraggable reads grocery items.
 *
 * Adding to a group goes through a popup with a search over the groups rather than one menu item per
 * group, which made the menu as long as the list of groups. The popup is portalled to the body so a
 * drag transform on the row cannot move it. The pick is saved once it has closed: an ungrouped idea
 * leaves the ungrouped list on joining a group, which would take the popup with it mid-animation.
 *
 * Every entry in the menu is online-only, so offline each is disabled, titled "Needs a connection".
 */
export default function MealIdeaRow({ idea, groupId = null, dragHandleProps = null, onRequestDelete, onOpen = null }) {
	const dispatch = useDispatch();
	const [choosingGroup, setChoosingGroup] = useState(false);
	const row = useRef(null);
	const pickedGroup = useRef(null);
	const selected = useSelector((state) => state.mealIdeas.selectedIdeaId === idea.id);
	const groups = useSelector((state) => {
		const index = selectMealIdeasIndex(state);
		return index.groupOrder.map((id) => index.groupsById[id]).filter(Boolean);
	}, shallowEqual);

	const offline = useOffline();
	const currentGroup = groups.find((group) => group.id === groupId);
	const isInGroup = (group) => group.meal_idea_ids.includes(idea.id);
	const items = [];
	if(groups.some((group) => !isInGroup(group))) {
		items.push({
			content: <span>Add to a group&hellip;</span>,
			onClick: () => setChoosingGroup(true),
			...onlineOnly(offline),
		});
	}
	if(currentGroup) {
		items.push({
			content: <span>Remove from {currentGroup.name}</span>,
			onClick: () => dispatch(removeMealIdeaFromGroup(idea.id, currentGroup.id)),
			...onlineOnly(offline),
		});
	}
	if(items.length) {
		items.push({ divider: true });
	}
	items.push({
		content: <span>Delete {idea.name}</span>,
		onClick: () => onRequestDelete('idea', idea.id, idea.name),
		...onlineOnly(offline),
	});

	return (
		<div
			ref={row}
			className={`meal_idea_row ${selected ? 'selected' : ''}`}
			data-id={idea.id}
			data-name={idea.name}
		>
			{dragHandleProps && <DragHandle className='' {...dragHandleProps} />}
			<span
				className='name'
				onClick={(event) => (onOpen ?
					onOpen(idea.id, event.currentTarget.closest('.meal_idea_row')) :
					dispatch(selectMealIdea(idea.id)))}
			>
				{idea.name}
			</span>
			<OptionsMenu
				id={`meal_idea_menu_${groupId === null ? 'ungrouped' : groupId}_${idea.id}`}
				items={items}
			/>
			{choosingGroup && ReactDOM.createPortal(
				<Modal
					className='meal_idea_group_picker_modal'
					header={`Add ${idea.name} to a group`}
					origin={row.current}
					onClose={() => {
						setChoosingGroup(false);
						if(pickedGroup.current) {
							dispatch(addMealIdeaToGroup(idea.id, pickedGroup.current.id));
							pickedGroup.current = null;
						}
					}}
				>
					{(close) => (
						<NamePicker
							items={groups}
							placeholder='Search groups'
							takenNote='Already in this group'
							emptyText='There are no groups yet.'
							isTaken={isInGroup}
							onPick={(group) => {
								pickedGroup.current = group;
								close();
							}}
						/>
					)}
				</Modal>,
				document.body,
			)}
		</div>
	);
}
